import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  CloudArrowUpIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import {
  uploadLargeFile,
  formatBytes,
  formatDuration,
} from '../services/uploadFileApi';

// ---------------------------------------------------------------------------
// Per-file upload state shape:
// { id, file, status: 'queued'|'uploading'|'completed'|'error'|'aborted',
//   progress, uploadedBytes, speedMBps, result, error, abortController }
// ---------------------------------------------------------------------------

let fileCounter = 0;

export default function VendorUpload() {
  const [token, setToken] = useState('');
  const [tokenValid, setTokenValid] = useState(null); // null = not checked, true/false
  const [tokenMessage, setTokenMessage] = useState('');
  const [uploads, setUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const dragZoneRef = useRef(null);

  // Read token from URL query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      setToken(urlToken);
      setTokenValid(true);
      setTokenMessage('Access token loaded from link.');
    }
  }, []);

  // -------------------------------------------------------------------------
  // Token validation
  // -------------------------------------------------------------------------
  const validateToken = async () => {
    if (!token.trim()) {
      setTokenValid(false);
      setTokenMessage('Please enter an access token.');
      return;
    }
    setTokenValid(null);
    setTokenMessage('Checking token...');
    try {
      const API_BASE_URL =
        process.env.REACT_APP_GOTTEA_API_BASE_URL ||
        'https://gottea-search-5gtrn.ondigitalocean.app';
      const res = await fetch(`${API_BASE_URL}/files/vendor-upload/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim() }),
      });
      if (res.ok) {
        setTokenValid(true);
        setTokenMessage('Token verified! You can now upload files.');
      } else {
        const err = await res.json().catch(() => ({}));
        setTokenValid(false);
        setTokenMessage(err.error || 'Invalid or expired token.');
      }
    } catch (e) {
      setTokenValid(false);
      setTokenMessage('Could not verify token. Please check your connection.');
    }
  };

  // -------------------------------------------------------------------------
  // Drag and drop handlers
  // -------------------------------------------------------------------------
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) addFiles(files);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, tokenValid]
  );

  // -------------------------------------------------------------------------
  // File selection via input
  // -------------------------------------------------------------------------
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) addFiles(files);
    e.target.value = ''; // reset so same file can be re-selected
  };

  // -------------------------------------------------------------------------
  // Add files to the upload queue and start uploading
  // -------------------------------------------------------------------------
  const addFiles = (files) => {
    if (!tokenValid) {
      setTokenValid(false);
      setTokenMessage('Please verify your access token before uploading.');
      return;
    }

    const newUploads = files.map((file) => ({
      id: ++fileCounter,
      file,
      status: 'queued',
      progress: 0,
      uploadedBytes: 0,
      speedMBps: 0,
      result: null,
      error: null,
      abortController: null,
      startTime: null,
    }));

    setUploads((prev) => [...prev, ...newUploads]);

    // Start uploading each file
    newUploads.forEach((u) => startUpload(u.id, u.file));
  };

  const updateUpload = (id, updates) => {
    setUploads((prev) =>
      prev.map((u) => (u.id === id ? { ...u, ...updates } : u))
    );
  };

  // -------------------------------------------------------------------------
  // Upload orchestration
  // -------------------------------------------------------------------------
  const startUpload = async (id, file) => {
    const abortController = new AbortController();
    updateUpload(id, {
      status: 'uploading',
      abortController,
      startTime: Date.now(),
      error: null,
    });

    try {
      const result = await uploadLargeFile({
        file,
        token: token.trim(),
        folder: 'vendor-uploads',
        concurrency: 3,
        signal: abortController.signal,
        onProgress: (uploadedBytes, totalBytes, speedMBps) => {
          const pct = totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0;
          updateUpload(id, {
            uploadedBytes,
            progress: pct,
            speedMBps,
          });
        },
      });

      updateUpload(id, {
        status: 'completed',
        progress: 100,
        result,
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        updateUpload(id, { status: 'aborted' });
      } else {
        console.error('Upload error:', e);
        updateUpload(id, { status: 'error', error: e.message });
      }
    }
  };

  const cancelUpload = (id) => {
    const upload = uploads.find((u) => u.id === id);
    if (upload?.abortController) {
      upload.abortController.abort();
    }
  };

  const removeUpload = (id) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const retryUpload = (id) => {
    const upload = uploads.find((u) => u.id === id);
    if (upload) {
      updateUpload(id, {
        status: 'queued',
        progress: 0,
        uploadedBytes: 0,
        speedMBps: 0,
        error: null,
        result: null,
      });
      startUpload(id, upload.file);
    }
  };

  // -------------------------------------------------------------------------
  // Derived stats
  // -------------------------------------------------------------------------
  const activeUploads = uploads.filter((u) => u.status === 'uploading');
  const completedUploads = uploads.filter((u) => u.status === 'completed');
  const totalBytesUploaded = uploads.reduce(
    (sum, u) => sum + u.uploadedBytes,
    0
  );
  const totalSize = uploads.reduce((sum, u) => sum + u.file.size, 0);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 rounded-2xl mb-4 shadow-lg">
            <CloudArrowUpIcon className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Footage Upload Portal
          </h1>
          <p className="text-gray-500">
            Drag and drop your files below to upload them securely. Large files
            (30-40 GB) are supported.
          </p>
        </div>

        {/* Token verification */}
        {tokenValid !== true && (
          <div className="bg-gt-white rounded-xl shadow-md p-6 mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Access Token
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Enter the access token you received, or use the link provided to
              you.
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={token}
                onChange={(e) => {
                  setToken(e.target.value);
                  setTokenValid(null);
                  setTokenMessage('');
                }}
                placeholder="Paste your access token here..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button
                onClick={validateToken}
                className="px-6 py-3 bg-teal-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
              >
                Verify
              </button>
            </div>
            {tokenMessage && (
              <div
                className={`mt-3 flex items-center gap-2 text-sm ${
                  tokenValid === true
                    ? 'text-green-600'
                    : tokenValid === false
                    ? 'text-red-600'
                    : 'text-gray-500'
                }`}
              >
                {tokenValid === true && (
                  <CheckCircleIcon className="w-5 h-5" />
                )}
                {tokenValid === false && (
                  <ExclamationCircleIcon className="w-5 h-5" />
                )}
                {tokenMessage}
              </div>
            )}
          </div>
        )}

        {/* Drag and drop zone */}
        {tokenValid === true && (
          <>
            <div
              ref={dragZoneRef}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200 cursor-pointer ${
                isDragging
                  ? 'border-teal-500 bg-teal-50 scale-[1.01]'
                  : 'border-gray-300 bg-gt-white hover:border-teal-400 hover:bg-teal-50/50'
              }`}
              onClick={() => document.getElementById('vendor-file-input').click()}
            >
              <input
                id="vendor-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
              <div className="flex flex-col items-center gap-4">
                <div
                  className={`p-4 rounded-full transition-colors ${
                    isDragging ? 'bg-teal-500' : 'bg-gray-100'
                  }`}
                >
                  <DocumentArrowUpIcon
                    className={`w-12 h-12 ${
                      isDragging ? 'text-white' : 'text-teal-500'
                    }`}
                  />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-700">
                    {isDragging
                      ? 'Drop your files here!'
                      : 'Drag and drop files here, or click to browse'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Supports multiple files · No size limit · Large video files
                    welcome
                  </p>
                </div>
              </div>
            </div>

            {/* Overall stats */}
            {uploads.length > 0 && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  label="Files"
                  value={String(uploads.length)}
                />
                <StatCard
                  label="Uploading"
                  value={String(activeUploads.length)}
                />
                <StatCard
                  label="Completed"
                  value={String(completedUploads.length)}
                />
                <StatCard
                  label="Total Uploaded"
                  value={formatBytes(totalBytesUploaded)}
                  sub={totalSize > 0 ? `of ${formatBytes(totalSize)}` : ''}
                />
              </div>
            )}

            {/* Upload list */}
            {uploads.length > 0 && (
              <div className="mt-6 space-y-4">
                {uploads.map((u) => (
                  <UploadCard
                    key={u.id}
                    upload={u}
                    onCancel={() => cancelUpload(u.id)}
                    onRemove={() => removeUpload(u.id)}
                    onRetry={() => retryUpload(u.id)}
                  />
                ))}
              </div>
            )}

            {/* Completed summary */}
            {completedUploads.length > 0 && (
              <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">
                    {completedUploads.length} file
                    {completedUploads.length > 1 ? 's' : ''} uploaded
                    successfully
                  </h3>
                </div>
                <p className="text-sm text-green-700">
                  Your files have been uploaded to our secure storage. We'll
                  process them and get back to you. Thank you!
                </p>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-400">
          <p>
            Need help? Contact your project coordinator.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
function StatCard({ label, value, sub }) {
  return (
    <div className="bg-gt-white rounded-xl shadow-sm p-4 text-center">
      <div className="text-2xl font-bold text-teal-500">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-file upload card
// ---------------------------------------------------------------------------
function UploadCard({ upload, onCancel, onRemove, onRetry }) {
  const { file, status, progress, uploadedBytes, speedMBps, result, error } =
    upload;
  const [copied, setCopied] = useState(false);

  // Estimate remaining time
  const remainingBytes = file.size - uploadedBytes;
  const etaSec = speedMBps > 0 ? remainingBytes / (speedMBps * 1024 * 1024) : 0;

  const copyLink = () => {
    if (result?.cdnUrl || result?.location) {
      const url = result.cdnUrl || result.location;
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const statusConfig = {
    queued: { label: 'Queued', color: 'text-gray-500', bg: 'bg-gray-100' },
    uploading: {
      label: 'Uploading',
      color: 'text-blue-600',
      bg: 'bg-blue-100',
    },
    completed: {
      label: 'Completed',
      color: 'text-green-600',
      bg: 'bg-green-100',
    },
    error: { label: 'Failed', color: 'text-red-600', bg: 'bg-red-100' },
    aborted: {
      label: 'Cancelled',
      color: 'text-gray-500',
      bg: 'bg-gray-100',
    },
  };

  const cfg = statusConfig[status] || statusConfig.queued;

  return (
    <div className="bg-gt-white rounded-xl shadow-md p-5">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} flex-shrink-0`}
          >
            {cfg.label}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-800 truncate" title={file.name}>
              {file.name}
            </p>
            <p className="text-sm text-gray-400">{formatBytes(file.size)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {status === 'uploading' && (
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
          {status === 'error' && (
            <>
              <button
                onClick={onRetry}
                className="px-3 py-1.5 text-sm text-teal-500 hover:bg-teal-50 rounded-lg transition-colors"
              >
                Retry
              </button>
              <button
                onClick={onRemove}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </>
          )}
          {(status === 'completed' || status === 'aborted') && (
            <button
              onClick={onRemove}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {(status === 'uploading' || status === 'completed') && (
        <div className="mb-2">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>
              {formatBytes(uploadedBytes)} / {formatBytes(file.size)}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-2.5 rounded-full transition-all duration-300 ${
                status === 'completed' ? 'bg-green-500' : 'bg-teal-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload speed + ETA */}
      {status === 'uploading' && speedMBps > 0 && (
        <div className="flex justify-between text-xs text-gray-400">
          <span>{speedMBps.toFixed(1)} MB/s</span>
          {etaSec > 0 && <span>~{formatDuration(etaSec)} remaining</span>}
        </div>
      )}

      {/* Error message */}
      {status === 'error' && error && (
        <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Completed result */}
      {status === 'completed' && result && (
        <div className="mt-3 flex items-center gap-2 bg-green-50 rounded-lg p-3">
          <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-700 truncate flex-1">
            {result.cdnUrl || result.location || result.key}
          </span>
          <button
            onClick={copyLink}
            className="text-xs text-teal-500 font-medium hover:underline flex-shrink-0"
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      )}

      {/* Aborted message */}
      {status === 'aborted' && (
        <p className="mt-2 text-sm text-gray-400">Upload was cancelled.</p>
      )}
    </div>
  );
}
